<template>
  <base-new-offer-item
    :title="title"
    :left-icon-src="leftIconSrc"
    :right-icon-src="rightIconSrc"
    @clicked="createBlankOffer"
  />
</template>

<script setup lang="ts">
import { computed } from "vue";
import BaseNewOfferItem from "@/views/TenderOffer/CreateNewOffer/BaseNewOfferItem.vue";
import cmsService from "@/services/cmsService";
import { useOfferTerminology } from "@/composables/useOfferTerminology";
import { Pages, Icon } from "@/stores/cms/types";

const props = defineProps<{
  pageCms: Pages;
}>();

const emit = defineEmits<{
  (e: "new-offer"): void;
}>();

// Get terminology state
const { isEstimateMode } = useOfferTerminology();

// Get the title from CMS, replacing "tilbud" with "estimat" if in estimate mode
const title = computed(() => {
  const text = props.pageCms.otherText?.find(
    (otherText) => otherText.label === "blankOfferButtonText",
  );

  // Get the original title
  const originalTitle = text ? text.title : "";
  // If we're in estimate mode, replace "tilbud" with "estimat"
  if (isEstimateMode.value) {
    return originalTitle?.replace(/tilbud/i, "estimat");
  }

  return originalTitle;
});

// Get icon URL helper function
const getIconUrl = (
  icons?: Array<Icon & { _key: string }> | Array<{ label: string; icon: Icon }>,
  label?: string,
) => {
  if (!icons || !label) return "";
  const matchingIcon = icons.find(
    (icon) => "label" in icon && icon.label === label,
  );
  return matchingIcon && "icon" in matchingIcon
    ? cmsService.resolveImage(matchingIcon.icon)
    : "";
};

// Compute icon sources
const leftIconSrc = computed(() =>
  getIconUrl(props.pageCms.icons, "newOfferIcon"),
);

const rightIconSrc = computed(() =>
  getIconUrl(props.pageCms.icons, "arrowRightIcon"),
);

// Emit new-offer event when clicked
const createBlankOffer = () => {
  emit("new-offer");
};
</script>

<style lang="scss" scoped>
.blank-offer-link {
  display: flex;
  align-items: center;
  text-decoration: none;
  color: $color-black;
  background: $color-primary-pink-dark;
  padding: 1rem;
  text-align: left;
  border-radius: 4px;

  &:hover {
    background: $color-primary-pink-darker;
  }

  &__text {
    flex-grow: 1;
    font-weight: 400;
    font-size: $font-size-base;
    margin-left: 0.5rem;
    margin-right: 0.5rem;
  }
}
</style>
